'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tab-scoped persistent state. Mirrors `useState` but writes JSON to
 * `sessionStorage[key]` so the value survives navigating away from and back
 * to the page (within the same tab).
 *
 * SSR-safe: the initial render always returns `defaultValue`. The stored value
 * is restored on the client in a `useEffect`, which avoids hydration mismatches.
 * Consumers that key off the value for an immediate UI decision should treat
 * the first paint as "default state" — typically fine for filter chips.
 */
export function useSessionStorageState<T>(key: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(defaultValue);
  const hydrated = useRef(false);

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(key);
      if (raw !== null) {
        setValue(JSON.parse(raw) as T);
      }
    } catch {
      // ignore — fall back to default
    }
    hydrated.current = true;
  }, [key]);

  useEffect(() => {
    if (!hydrated.current) return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // quota / private mode — ignore
    }
  }, [key, value]);

  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue(next);
  }, []);

  return [value, set];
}
